import { workEmailError } from './workEmail';
import { saveAs } from 'file-saver';
import catalog from '../../data/landingSamples.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public-samples`;
const read = (store, key) => { try { return JSON.parse(window[store].getItem(key)); } catch { return null; } };
const write = (store, key, value) => { try { window[store].setItem(key, JSON.stringify(value)); } catch { /* Browser storage may be disabled. */ } };
export const existingSession = () => { try { return localStorage.getItem('session_token'); } catch { return null; } };
let transientVisitor;
export const measurementAllowed = () => navigator.doNotTrack !== '1' && navigator.globalPrivacyControl !== true;
export const visitorId = () => {
  const saved = read('localStorage', 'bds_sample_visitor');
  if (saved && Date.now() - saved.created < 86400000) return saved.id;
  transientVisitor ||= crypto.randomUUID();
  write('localStorage', 'bds_sample_visitor', { id: transientVisitor, created: Date.now() });
  return transientVisitor;
};
export const deviceType = () => window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';
export const sampleContext = sample => ({ visitor_id: visitorId(), sample, device: deviceType() });
export const accessToken = () => {
  const access = read('sessionStorage', 'bds_sample_access');
  return access && new Date(access.expires_at).getTime() > Date.now() ? access.sample_access : null;
};
const headers = () => ({ 'Content-Type': 'application/json', ...(accessToken() ? { 'X-Sample-Access': accessToken() } : {}), ...(existingSession() ? { Authorization: `Bearer ${existingSession()}` } : {}) });
export const trackSample = (event, sample = 'company') => {
  if (!measurementAllowed()) return;
  fetch(`${API}/events`, { method: 'POST', headers: headers(), keepalive: true,
    body: JSON.stringify({ ...sampleContext(sample), event, event_id: crypto.randomUUID() }) }).catch(() => {});
};
export const requestSampleAccess = async (email, consent, sample, requestedDownload, entryPoint) => {
  const validation = workEmailError(email);
  if (validation) throw new Error(validation);
  const response = await fetch(`${API}/access`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...sampleContext(sample), email, marketing_consent: consent, measure: measurementAllowed(), requested_download: requestedDownload, entry_point: entryPoint }) });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.sample_access) throw new Error(typeof result.detail === 'string' ? result.detail : 'Sample access could not be saved. Check your email and try again.');
  write('sessionStorage', 'bds_sample_access', result);
  return result;
};
export const downloadSample = async (kind, sample, access) => {
  const response = await fetch(`${API}/download/${kind}`, { headers: { ...headers(), ...(access ? { 'X-Sample-Access': access } : {}) } });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body.detail || 'The download failed. Your chosen example is still open; please retry.');
    if (response.status === 403) {
      try { sessionStorage.removeItem('bds_sample_access'); } catch { /* optional browser storage */ }
      error.needsSampleAccess = true;
    }
    throw error;
  }
  const bytes = await response.arrayBuffer();
  const expected = catalog.downloads[kind];
  const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(b => b.toString(16).padStart(2, '0')).join('');
  if (expected.sha256 && hash !== expected.sha256) throw new Error('The sample version changed. Reload the page before downloading again.');
  saveAs(new Blob([bytes], { type: kind === 'pdf' ? 'application/pdf' : 'application/zip' }), expected.filename);
  trackSample('download_completed', sample);
};
export const saveTrialIntent = (subject, sample) => write('localStorage', 'bds_sample_intent', { subject: subject.trim().slice(0, 200), sample, visitor: visitorId(), created: Date.now() });
export const trialIntent = () => {
  const intent = read('localStorage', 'bds_sample_intent');
  return intent && Date.now() - intent.created < 30 * 60 * 1000 ? intent : null;
};
export const clearTrialIntent = () => { try { localStorage.removeItem('bds_sample_intent'); } catch { /* optional handoff */ } };
export const attributeTrial = intent => measurementAllowed() ? fetch(`${API}/attribution`, { method: 'POST', headers: headers(), body: JSON.stringify({ visitor_id: intent.visitor }) }).catch(() => {}) : Promise.resolve();
export const reportOpenedFromSample = reportType => {
  if (!measurementAllowed() || !existingSession()) return;
  fetch(`${API}/report-opened`, { method: 'POST', headers: headers(), body: JSON.stringify({ report_type: reportType }) }).catch(() => {});
};